/**
 * Pemformatan angka, uang, dan waktu dalam konvensi Indonesia.
 *
 * Seluruh nilai uang datang dari API sebagai bilangan bulat rupiah penuh —
 * tidak pernah pecahan — sehingga tidak ada pembulatan yang perlu dilakukan
 * di sisi klien. Yang dikerjakan di sini murni penyajian.
 */

const rupiah = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
})

const number = new Intl.NumberFormat('id-ID')

const compact = new Intl.NumberFormat('id-ID', {
  notation: 'compact',
  maximumFractionDigits: 1,
})

export function formatRupiah(value: number): string {
  return rupiah.format(value)
}

/** formatRupiahShort dipakai pada kartu statistik yang ruangnya sempit. */
export function formatRupiahShort(value: number): string {
  if (Math.abs(value) < 1_000_000) return rupiah.format(value)
  return `Rp ${compact.format(value)}`
}

/**
 * formatAmountAxis dipakai pada sumbu grafik.
 *
 * Satuan rupiah tidak diulang pada setiap label: judul grafik dan tooltip
 * sudah menyebutnya, sedangkan label sumbu yang panjang justru terpotong
 * atau membungkus ke dua baris.
 */
export function formatAmountAxis(value: number): string {
  if (value === 0) return '0'
  return compact.format(value)
}

export function formatNumber(value: number): string {
  return number.format(value)
}

const dateFormat = new Intl.DateTimeFormat('id-ID', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

const dateShortFormat = new Intl.DateTimeFormat('id-ID', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
})

const dateTimeFormat = new Intl.DateTimeFormat('id-ID', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

export function formatDate(value: string | Date): string {
  return dateFormat.format(new Date(value))
}

export function formatDateShort(value: string | Date): string {
  return dateShortFormat.format(new Date(value))
}

export function formatDateTime(value: string | Date): string {
  return dateTimeFormat.format(new Date(value))
}

const relative = new Intl.RelativeTimeFormat('id-ID', { numeric: 'auto' })

const relativeSteps: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 1000 * 60 * 60 * 24 * 365],
  ['month', 1000 * 60 * 60 * 24 * 30],
  ['day', 1000 * 60 * 60 * 24],
  ['hour', 1000 * 60 * 60],
  ['minute', 1000 * 60],
]

/** formatRelative mengubah waktu menjadi "3 hari lalu" atau "dalam 2 jam". */
export function formatRelative(value: string | Date): string {
  const delta = new Date(value).getTime() - Date.now()

  for (const [unit, ms] of relativeSteps) {
    if (Math.abs(delta) >= ms) {
      return relative.format(Math.round(delta / ms), unit)
    }
  }
  return 'baru saja'
}

/** formatDuration mengubah menit menjadi "2 jam 15 menit". */
export function formatDuration(minutes: number): string {
  if (minutes <= 0) return '—'
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours === 0) return `${rest} menit`
  if (rest === 0) return `${hours} jam`
  return `${hours} jam ${rest} menit`
}

/** formatCountdown mengubah sisa detik menjadi "01:59:04". */
export function formatCountdown(totalSeconds: number): string {
  const safe = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(safe / 3600)
  const minutes = Math.floor((safe % 3600) / 60)
  const seconds = safe % 60
  const pad = (value: number) => value.toString().padStart(2, '0')
  return hours > 0 ? `${pad(hours)}:${pad(minutes)}:${pad(seconds)}` : `${pad(minutes)}:${pad(seconds)}`
}

export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}
